import React, { useCallback, useRef, useState, useEffect, useHistory } from "react";
import axios from 'axios';
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '../../components/utils/Tooltip';
// import { faChild, faEraser, faUpload, faDownload, faFilePdf, faTextHeight, faTimes, faTruckMedical  } from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from "jspdf";

const BlankCanvas = ({ width = 400, height = 500, onClose }) => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [userId, setUserId] = useState(() => authState.user._id);
  const [userCode, setUserCode] = useState(() => authState.user.think5whyscode);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [convertToTextClicked, setConvertToTextClicked] = useState(false);

  const [convertedText, setConvertedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const drawColor = 'black';

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = drawColor;
    ctx.lineWidth = 1; 
  }, [width, height]);

  // Handle mouse and touch events
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const startDrawing = (x, y) => {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    };

    const draw = (x, y) => {
      if (!isDrawing) return;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const endDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        ctx.beginPath(); // Reset the path
      }
    };

    // Mouse Events
    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleMouseUp = endDrawing;
    const handleMouseOut = endDrawing;

    // Touch Events
    const handleTouchStart = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleTouchEnd = endDrawing;

    // Add event listeners for mouse and touch
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseout', handleMouseOut);

    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      // Remove event listeners for mouse and touch
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseout', handleMouseOut);

      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing]);

  const getCurrentTimestamp = () => {
    const now = new Date();
    const yyyymmdd = now.toISOString().slice(0, 10).replace(/-/g, ""); // YYYYMMDD
    const hhmmss = now.toTimeString().slice(0, 8).replace(/:/g, ""); // HHMMSS
    return `${yyyymmdd}${hhmmss}`;
  };

  const [uploadImageClicked, setUploadImageClicked] = useState(false);
  const convertBase64ToBlob = (base64String) => {
    const byteString = atob(base64String.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: 'image/png' });
  };

  const handleUploadImage = async () => {
    try {
      setUploadImageClicked(true);
      setError(""); // Reset previous errors
      setLoading(true);

      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error("Canvas not found.");
      }

      const imageDataURL = canvas.toDataURL("image/png");
      // console.log("✅ Image Data URL (Base64):", imageDataURL.substring(0, 50) + "..."); // Log first 50 chars

      const imageBlob = convertBase64ToBlob(imageDataURL);
      // console.log("Image Blob:", imageBlob);

      const formData = new FormData();
      const imgFileName = `touchbook_${userCode}_${getCurrentTimestamp()}.png`;    

      formData.append("file", imageBlob, imgFileName);

      // console.log("✅ FormData prepared:", formData.get("file")); // Ensure file is appended

      // Upload to backend
      const response = await axios.post("/api/uploads/upload-touchbook-pic", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        alert("Image uploaded successfully!");
      } else {
        throw new Error("Failed to upload image. Server responded with status: " + response.status);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setError(error.message || "An error occurred during upload.");
    } finally {
      setLoading(false);
    }
  };

  const [downlaodImageClicked, setDownlaodImageClicked]=useState(false);
  const handleDownloadImage = async () => {
    setDownlaodImageClicked(true);
    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not found.');
      setLoading(false);
      return;
    }
  
    const imageDataURL = canvas.toDataURL('image/png');
  
    // Convert base64 to Blob
    const byteString = atob(imageDataURL.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const imageBlob = new Blob([ab], { type: 'image/png' });
  
    // Create a temporary URL and trigger the download
    const blobURL = URL.createObjectURL(imageBlob);
    const link = document.createElement('a');
    link.href = blobURL;
    const imgFileName = `touchbook_${userCode}_${getCurrentTimestamp()}.png`;    
    link.download = imgFileName; 
    document.body.appendChild(link);
    link.click();
  
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(blobURL);
  };
  

  const handleDownloadPDF = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not found.');
      return;
    }
  
    const imageDataURL = canvas.toDataURL('image/png');
  
    // Create a PDF and add the image
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210; 
    const imgHeight = (canvas.height / canvas.width) * imgWidth; 
  
    pdf.addImage(imageDataURL, 'PNG', 0, 0, imgWidth, imgHeight);
    const pdfFileName = `touchbook_${userCode}_${getCurrentTimestamp()}`;    
    pdf.save(pdfFileName); 
  };

  const handleConvertToText = async () => {
    setConvertToTextClicked(true);
    setLoading(true);
    setError('');
    setConvertedText('');
  
    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not found.');
      setLoading(false);
      return;
    }
  
    const imageDataURL = canvas.toDataURL('image/png'); 
  
    // Convert base64 image to a Blob (file)
    const byteString = atob(imageDataURL.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const imageBlob = new Blob([ab], { type: 'image/png' });  

    const formData = new FormData();
    formData.append('file', imageBlob, 'image.png');  
    formData.append('language', 'eng');
    formData.append('apikey', 'K81755459088957'); 

    // console.log(imageDataURL);

  
    try {
      const response = await fetch('https://api.ocr.space/parse/image', {
        method: 'POST',
        body: formData,
      });
  
      const data = await response.json();
      // console.log("OCR response:", data);
  
      if (data.IsErroredOnProcessing) {
        setError(data.ErrorMessage || 'Error processing image.');
        setConvertedText('');
      } else {
        setConvertedText(data.ParsedResults[0]?.ParsedText || 'No text found');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while processing the image.');
      setConvertedText('');
    } finally {
      setLoading(false);
    }
  };
  

  const handleSaveText = async () => {
    // try {
    //   await axios.post('http://localhost:5000/save-text', { text: convertedText });
    //   alert('Text extracted and saved successfully!');
    // } catch (error) {
    //   console.error('Error saving text:', error);
    //   alert('Failed to save text.');
    // }
  };

  const onCancel = async () => {
    setConvertToTextClicked(false);
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
  };

  const [selectedButton, setSelectedButton] = useState(null);

  const handleButtonClick = (buttonName, callback) => {
    setSelectedButton(buttonName);
    if (callback) callback();
  };

  return (
    <div className="mt-4 ml-4 mr-4 py-4 px-4" style={{ marginTop: '20px', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          border: '1px solid #ccc',
          backgroundColor: 'white',
          display: 'block',
          margin: '0 auto',
        }}
        className="canvas"
      />
      <div className="flex flex-row items-center justify-center px-2 py-2 mt-2 space-x-2">
        <Tooltip className="w-12 text-yellow-200" text="Undo" position="top">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === "undo" ? "bg-blue-400" : "bg-blue-600 text-white"
            }`}
            onClick={() => handleButtonClick("undo", clearCanvas)}
          >
            <i className="fa-solid fa-undo"></i>
          </span>
        </Tooltip>

        <Tooltip className="w-12" text="Upload" position="top">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === "upload" ? "bg-blue-400" : "bg-blue-600 text-white"
            }`}
            onClick={() => handleButtonClick("upload", handleUploadImage)}
          >
            <i className="fa-solid fa-upload"></i>
          </span>
        </Tooltip>

        <Tooltip className="w-12" text="Download Image" position="top">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === "downloadImage" ? "bg-blue-400" : "bg-blue-600 text-white"
            }`}
            onClick={() => handleButtonClick("downloadImage", handleDownloadImage)}
          >
            <i className="fa-solid fa-download"></i>
          </span>
        </Tooltip>

        <Tooltip className="w-12" text="Download PDF" position="top">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === "downloadPDF" ? "bg-blue-400" : "bg-blue-600 text-white"
            }`}
            onClick={() => handleButtonClick("downloadPDF", handleDownloadPDF)}
          >
            <i className="fa-solid fa-file-pdf"></i>
          </span>
        </Tooltip>

        <Tooltip className="w-12" text="Extract Text" position="top">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === "downloadPDF" ? "bg-blue-400" : "bg-blue-600 text-white"
            }`}
            onClick={() => handleButtonClick("convertTotext", handleConvertToText)}

            // onClick={handleConvertToText}
          >
            <i className="fa-solid fa-file-lines"></i>
          </span>
        </Tooltip>

        <Tooltip className="w-12" text="Close" position="top">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === "close" ? "bg-blue-400" : "bg-blue-600 text-white"
            }`}
            onClick={() => handleButtonClick("close", onClose)}
          >
            <i className="fa-solid fa-arrow-right-from-bracket"></i>
          </span>
        </Tooltip>
      </div>
      
      {/* Display Converted Text */}
      {convertToTextClicked && (
        <div className='mt-4'>
          <div style={{ marginTop: '2px', marginLeft: '10px' }}>
            <p className='px-2 text-sm'>Converted Text (Editable):</p>
            <textarea
              value={convertedText}
              onChange={(e) => setConvertedText(e.target.value)}
              className='text-black bg-white px-2 py-2'
              style={{ maxWidth: '100%', height: '300px', fontSize: '12px' }}
            />
          </div>
          <div className='flex flex-row justify-center px-2'>
            <Tooltip className="w-12" text="Extract Text" position="top">
              <span
                className={`w-12 h-8 mr-4 flex items-center justify-center cursor-pointer rounded-lg 
                  ${selectedButton === "downloadPDF" 
                    ? convertedText === "No text found" ? "bg-blue-400" : "bg-gray-300" 
                    : convertedText === "No text found" ? "bg-gray-400 text-black" : "bg-blue-600 text-white"}`}
                onClick={convertedText === "No text found" ? () => handleButtonClick("saveText", handleSaveText) : undefined}
              >
                <i className="fa-solid fa-database"></i>
              </span>
            </Tooltip>

            <Tooltip className="w-12" text="Close" position="top">
              <span
                className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
                  selectedButton === "close" ? "bg-blue-400" : "bg-blue-600 text-white"
                }`}
                onClick={() => handleButtonClick("cancel", onCancel)}
              >
                <i className="fa-solid fa-window-close"></i>
              </span>
            </Tooltip>
          </div>
          <div className='flex flex-col justify-center'>
            {loading && <p>Loading...</p>}
            {error && <p style={{ color: 'red' }}>{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlankCanvas;
